import React, { useEffect, useState } from 'react';
import {
  Alert,
  ArrowLeftIcon,
  Box,
  Button,
  Checkbox,
  CloseIcon,
  PlusIcon,
  Skeleton,
  Switch,
  Text,
  TextInput,
} from '@razorpay/blade/components';
import { useHistory } from 'react-router-dom';
import { EmployeeDataGeneralSettings } from 'api/gql-utils/employeeDataSettings/types';
import { getAppErrorDetails } from 'utils/AppError';
import { GENERAL_SETTINGS_SKELETON_PANEL_COUNT } from '../constants';
import { FixedFooter, InlineActionButton, RadioOption } from '../styles';
import {
  useEmployeeDataGeneralSettings,
  useUpdateEmployeeDataGeneralSettings,
} from '../hooks';

// These mirror the monolith validation rules for the employee-data general
// settings update endpoint (GraphEmployeeDataSettingsController::updateGeneral).
// Keep them in sync with the server so we surface errors before hitting a 422.
export const EMPLOYEE_ID_PREFIX_MAX_LENGTH = 40;
export const EMPLOYEE_ID_LENGTH_MAX = 20;
export const CONTRACTOR_ID_PREFIX_MAX_LENGTH = 10;

export const GeneralTab = () => {
  const history = useHistory();
  const query = useEmployeeDataGeneralSettings();
  const updateMutation = useUpdateEmployeeDataGeneralSettings();
  const [settings, setSettings] = useState<EmployeeDataGeneralSettings | null>(null);

  useEffect(() => {
    if (query.data) setSettings(query.data);
  }, [query.data]);

  if (query.error) {
    // Load failures reject with a thrown error, so the success envelope's `message`
    // is unavailable; surface the GraphQL extensions message when present.
    const apiErrorMessage = getAppErrorDetails(query.error).otherDetails
      ?.errorStringFromExtensionMessages;

    return (
      <Alert
        color="negative"
        title="Could not load employee data settings"
        description={apiErrorMessage || 'Refresh the page or try again in a few minutes.'}
      />
    );
  }

  if (query.isLoading || !settings) {
    return (
      <Box width="100%" maxWidth="720px" marginX="auto" paddingBottom="spacing.11">
        <Box display="flex" flexDirection="column" gap="spacing.7">
          {Array.from({ length: GENERAL_SETTINGS_SKELETON_PANEL_COUNT }).map((_, panelIndex) => (
            <Box
              key={`general-settings-skeleton-${panelIndex}`}
              backgroundColor="surface.background.gray.intense"
              borderWidth="thin"
              borderColor="surface.border.gray.subtle"
              borderRadius="large"
              padding="spacing.6">
              <Skeleton width="40%" height="spacing.7" />
              <Box marginTop="spacing.4">
                <Skeleton width="100%" height="88px" />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  const updateSettings = (nextSettings: EmployeeDataGeneralSettings) => setSettings(nextSettings);

  const updateEmployeeIds = (nextValue: Partial<EmployeeDataGeneralSettings['employeeIds']>) =>
    updateSettings({ ...settings, employeeIds: { ...settings.employeeIds, ...nextValue } });

  const getSavePayload = () => ({
    employeeIds: settings.employeeIds,
    employeeDirectory: settings.employeeDirectory,
    payslip: settings.payslip,
  });

  const isAutoMode = settings.employeeIds.mode === 'auto';
  const employeeIdPrefixError =
    (settings.employeeIds.employeeIdPrefix?.length ?? 0) > EMPLOYEE_ID_PREFIX_MAX_LENGTH
      ? `Prefix cannot exceed ${EMPLOYEE_ID_PREFIX_MAX_LENGTH} characters.`
      : '';
  const employeeIdLengthError =
    settings.employeeIds.employeeIdLength > EMPLOYEE_ID_LENGTH_MAX
      ? `ID length cannot exceed ${EMPLOYEE_ID_LENGTH_MAX}.`
      : '';
  const contractorIdPrefixError =
    settings.employeeIds.enableSeparateContractorIdSeries &&
    (settings.employeeIds.contractorIdPrefix?.length ?? 0) > CONTRACTOR_ID_PREFIX_MAX_LENGTH
      ? `Prefix cannot exceed ${CONTRACTOR_ID_PREFIX_MAX_LENGTH} characters.`
      : '';
  const hasValidationErrors =
    isAutoMode &&
    Boolean(employeeIdPrefixError || employeeIdLengthError || contractorIdPrefixError);

  return (
    <>
      <Box width="100%" maxWidth="720px" marginX="auto" paddingBottom="spacing.11">
        <Box display="flex" flexDirection="column" gap="spacing.7">
          <Box
            backgroundColor="surface.background.gray.intense"
            borderWidth="thin"
            borderColor="surface.border.gray.subtle"
            borderRadius="large"
            padding="spacing.6">
            <Text size="large" weight="semibold">
              Employee & Contractor IDs
            </Text>
            <Text size="small" marginTop="spacing.2" color="surface.text.gray.muted">
              How IDs are generated and applied to new joiners
            </Text>

            <Box
              width="100%"
              marginY="spacing.5"
              borderBottomWidth="thin"
              borderBottomColor="surface.border.gray.subtle"
            />

            <Box display="flex" flexDirection="column" gap="spacing.7">
              <RadioOption>
                <input
                  type="radio"
                  checked={settings.employeeIds.mode === 'auto'}
                  onChange={() => updateEmployeeIds({ mode: 'auto' })}
                />
                <Box>
                  <Text weight="semibold">
                    Auto generate IDs
                  </Text>
                  <Text size="small" marginTop="spacing.1" color="surface.text.gray.muted">
                    Each new joiner gets the next ID in a numbered series
                  </Text>
                </Box>
              </RadioOption>

              {settings.employeeIds.mode === 'auto' ? (
                <Box marginLeft="spacing.6" display="flex" flexDirection="column" gap="spacing.4">
                  <Box
                    display="grid"
                    gridTemplateColumns={{
                      base: 'minmax(0, 1fr)',
                      m: 'repeat(2, minmax(0, 1fr))',
                    }}
                    gap="spacing.4">
                    <TextInput
                      label="Employee ID prefix"
                      value={settings.employeeIds.employeeIdPrefix}
                      maxCharacters={EMPLOYEE_ID_PREFIX_MAX_LENGTH}
                      validationState={employeeIdPrefixError ? 'error' : 'none'}
                      errorText={employeeIdPrefixError}
                      onChange={({ value }) => updateEmployeeIds({ employeeIdPrefix: value ?? '' })}
                    />
                    <TextInput
                      label="ID length"
                      type="number"
                      value={String(settings.employeeIds.employeeIdLength)}
                      validationState={employeeIdLengthError ? 'error' : 'none'}
                      errorText={employeeIdLengthError}
                      onChange={({ value }) =>
                        updateEmployeeIds({ employeeIdLength: Number(value ?? 0) })
                      }
                    />
                  </Box>

                  {settings.employeeIds.enableSeparateContractorIdSeries ? (
                    <>
                      <Box width={{ base: '100%', m: 'calc(50% - 4px)' }}>
                        <TextInput
                          label="Contractor ID prefix"
                          value={settings.employeeIds.contractorIdPrefix}
                          maxCharacters={CONTRACTOR_ID_PREFIX_MAX_LENGTH}
                          validationState={contractorIdPrefixError ? 'error' : 'none'}
                          errorText={contractorIdPrefixError}
                          onChange={({ value }) =>
                            updateEmployeeIds({ contractorIdPrefix: value ?? '' })
                          }
                        />
                      </Box>
                      <InlineActionButton
                        type="button"
                        color="negative"
                        onClick={() =>
                          updateEmployeeIds({
                            enableSeparateContractorIdSeries: false,
                            contractorIdPrefix: '',
                          })
                        }
                      >
                        <CloseIcon size="medium" color="feedback.icon.negative.intense" />
                        Remove contractor series
                      </InlineActionButton>
                    </>
                  ) : (
                    <InlineActionButton
                      type="button"
                      onClick={() =>
                        updateEmployeeIds({
                          enableSeparateContractorIdSeries: true,
                        })
                      }
                    >
                      <PlusIcon size="medium" color="interactive.icon.primary.normal" />
                      Add separate series for contractors
                    </InlineActionButton>
                  )}
                </Box>
              ) : null}

              <RadioOption>
                <input
                  type="radio"
                  checked={settings.employeeIds.mode === 'manual'}
                  onChange={() => updateEmployeeIds({ mode: 'manual' })}
                />
                <Box>
                  <Text weight="semibold">
                    Enter IDs manually
                  </Text>
                  <Text size="small" marginTop="spacing.1" color="surface.text.gray.muted">
                    You set each person's ID on their profile
                  </Text>
                </Box>
              </RadioOption>
            </Box>
          </Box>

          <Box
            backgroundColor="surface.background.gray.intense"
            borderWidth="thin"
            borderColor="surface.border.gray.subtle"
            borderRadius="large"
            padding="spacing.6">
            <Text size="large" weight="semibold">
              Payslip
            </Text>
            <Text size="small" marginTop="spacing.2" color="surface.text.gray.muted">
              Choose what prints on every payslip
            </Text>

            <Box
              width="100%"
              marginY="spacing.5"
              borderBottomWidth="thin"
              borderBottomColor="surface.border.gray.subtle"
            />

            <Box display="flex" flexDirection="column" gap="spacing.4">
              <Checkbox
                isChecked={settings.payslip.showEmployeeId}
                onChange={({ isChecked }) =>
                  updateSettings({
                    ...settings,
                    payslip: { ...settings.payslip, showEmployeeId: isChecked },
                  })
                }
              >
                Employee ID
              </Checkbox>
              <Checkbox
                isChecked={settings.payslip.showBrandName}
                onChange={({ isChecked }) =>
                  updateSettings({
                    ...settings,
                    payslip: { ...settings.payslip, showBrandName: isChecked },
                  })
                }
              >
                Company brand name
              </Checkbox>
              <Checkbox
                isChecked={settings.payslip.showCompanyAddress}
                onChange={({ isChecked }) =>
                  updateSettings({
                    ...settings,
                    payslip: { ...settings.payslip, showCompanyAddress: isChecked },
                  })
                }
              >
                Company address
              </Checkbox>
            </Box>
          </Box>

          <Box
            backgroundColor="surface.background.gray.intense"
            borderWidth="thin"
            borderColor="surface.border.gray.subtle"
            borderRadius="large"
            padding="spacing.6">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Text size="large" weight="semibold">
                  Employee Directory
                </Text>
                <Text size="small" marginTop="spacing.2" color="surface.text.gray.muted">
                  A searchable team directory for everyone in your organisation
                </Text>
              </Box>
              <Switch
                accessibilityLabel="Employee Directory"
                isChecked={settings.employeeDirectory.enabled}
                onChange={({ isChecked }) =>
                  updateSettings({ ...settings, employeeDirectory: { enabled: isChecked } })
                }
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <FixedFooter>
        <Box
          width="100%"
          maxWidth="720px"
          marginX="auto"
          display="flex"
          justifyContent="space-between"
          alignItems="center">
          <Button variant="secondary" icon={ArrowLeftIcon} onClick={() => history.push('/settings')}>
            Back
          </Button>
          <Button
            isLoading={updateMutation.isLoading}
            isDisabled={hasValidationErrors}
            onClick={() =>
              updateMutation.mutate(getSavePayload(), {
                onSuccess: () => history.push('/settings'),
              })
            }
          >
            Save & Close
          </Button>
        </Box>
      </FixedFooter>
    </>
  );
};
